import { useCallback, useEffect, useState } from 'react';

import { ApiError, documentFileUrl, listPhotos, type PhotoRow } from '../api.js';
import { formatDate } from '../labels.js';

/**
 * The photo gallery (ST-06) — every image on the project, with what it is OF.
 *
 * There is no photo upload here on purpose. A photo is uploaded WHERE IT IS
 * EVIDENCE — on the task, the snag, the diary, the activity — and this screen
 * is the cross-cut: "show me everything from Level 3", "everything from last
 * week", "everything on that snag". An upload button here would create exactly
 * the untagged photo pile the per-record flow exists to prevent.
 *
 * Filters run in the browser, like the portfolio's: the list is already
 * loaded, filtering is instant, and the day this needs pagination is the day
 * the filters move server-side with it.
 */

const ENTITY_WORDS: Readonly<Record<string, string>> = {
  task: 'Task',
  snag: 'Snag',
  activity: 'Activity',
  diary: 'Site diary',
  material_item: 'Material',
  drawing_revision: 'Drawing',
  submittal: 'Submittal',
  ncr: 'NCR',
  inspection: 'Inspection',
  checklist_response: 'Inspection line',
  handover_item: 'Handover',
  verification: 'Verification',
  milestone: 'Milestone',
  project: 'Project',
};

interface Props {
  readonly projectId: string;
}

export function Photos({ projectId }: Props): JSX.Element {
  const [items, setItems] = useState<readonly PhotoRow[] | null>(null);
  /** Not the same as no photos — see `Drawings.tsx`. */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [kind, setKind] = useState('');
  const [where, setWhere] = useState('');
  const [trade, setTrade] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = useCallback(async () => {
    try {
      setItems(await listPhotos(projectId));
      setDenied(false);
    } catch (caught) {
      const refused =
        caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the photos.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Photos</h3>
    </div>
  );

  if (denied) {
    return (
      <>
        {head}
        <p className="hint">Your role does not include this project&rsquo;s photos.</p>
      </>
    );
  }

  if (!items) {
    return (
      <>
        {head}
        {error ? (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        ) : (
          <p aria-live="polite">Loading the photos…</p>
        )}
      </>
    );
  }

  const kinds = [...new Set(items.map((p) => p.entity_type))].sort();
  const places = [...new Set(items.map((p) => p.location_path).filter(Boolean))].sort() as string[];
  const trades = [...new Set(items.map((p) => p.trade_name).filter(Boolean))].sort() as string[];

  const shown = items.filter((p) => {
    if (kind && p.entity_type !== kind) return false;
    // Subtree match: "Level 3" also finds "Level 3/Zone B".
    if (where && !(p.location_path ?? '').startsWith(where)) return false;
    if (trade && p.trade_name !== trade) return false;
    if (from && p.day < from) return false;
    if (to && p.day > to) return false;
    return true;
  });

  const filtering = Boolean(kind || where || trade || from || to);

  return (
    <>
      {head}

      {items.length === 0 ? (
        <div className="empty">
          <p>
            <strong>No photos yet.</strong>
          </p>
          <p>
            Photos are added where they are evidence — on a task, a snag, a diary day, an
            activity — and they all show up here, filterable by place, trade and date.
          </p>
        </div>
      ) : (
        <>
          <div className="filter-row">
            <div className="field">
              <label htmlFor="ph-kind">Attached to</label>
              <select id="ph-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="">Anything</option>
                {kinds.map((key) => (
                  <option key={key} value={key}>
                    {ENTITY_WORDS[key] ?? key}
                  </option>
                ))}
              </select>
            </div>
            {places.length > 0 && (
              <div className="field">
                <label htmlFor="ph-where">Place</label>
                <select id="ph-where" value={where} onChange={(e) => setWhere(e.target.value)}>
                  <option value="">Anywhere</option>
                  {places.map((path) => (
                    <option key={path} value={path}>
                      {path}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {trades.length > 0 && (
              <div className="field">
                <label htmlFor="ph-trade">Trade</label>
                <select id="ph-trade" value={trade} onChange={(e) => setTrade(e.target.value)}>
                  <option value="">Any trade</option>
                  {trades.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="field">
              <label htmlFor="ph-from">From</label>
              <input
                id="ph-from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="ph-to">To</label>
              <input id="ph-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            {filtering && (
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setKind('');
                  setWhere('');
                  setTrade('');
                  setFrom('');
                  setTo('');
                }}
              >
                Clear filters
              </button>
            )}
          </div>

          {shown.length === 0 ? (
            <div className="empty">
              <p>
                <strong>Nothing matches those filters.</strong>
              </p>
              <p>
                There {items.length === 1 ? 'is 1 photo' : `are ${items.length} photos`} on the
                project — none of them match.
              </p>
            </div>
          ) : (
            <ul className="photo-grid">
              {shown.map((photo) => (
                <li key={photo.attachment_id} className="photo-cell">
                  {/*
                    THE THUMBNAIL IS THE FILE. Both the `img` and the link fetch
                    the same bytes through the same route, so a photo the server
                    will not serve gets neither — a grid of broken image icons
                    with no explanation is worse than a sentence saying why.
                  */}
                  {photo.can_download ? (
                    <a href={documentFileUrl(projectId, photo.document_id)} download>
                      <img
                        src={documentFileUrl(projectId, photo.document_id)}
                        alt={
                          photo.entity_label
                            ? `${ENTITY_WORDS[photo.entity_type] ?? photo.entity_type}: ${photo.entity_label}`
                            : photo.filename
                        }
                        loading="lazy"
                      />
                    </a>
                  ) : (
                    <span className="photo-withheld">
                      {photo.scan_status === 'infected'
                        ? 'Malware found in this photo. It cannot be opened.'
                        : 'This photo has not been checked for malware yet, so it cannot be opened.'}
                    </span>
                  )}
                  <span className="photo-caption">
                    {photo.entity_label && <strong>{photo.entity_label}</strong>}
                    <span className="hint">
                      {formatDate(photo.day)}
                      {photo.location_path && ` · ${photo.location_path}`}
                      {photo.trade_name && ` · ${photo.trade_name}`}
                      {photo.is_external_visible ? ' · shared' : ' · internal'}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </>
  );
}
